import React, { useState } from 'react';
import { BACKEND_URL } from '../../api/api';

/**
 * Componente para importar usuarios masivamente desde un archivo Excel
 * @param {Function} onImportSuccess - Callback cuando la importación es exitosa
 */
export default function ImportarUsuarios({ onImportSuccess }) {
  const [archivo, setArchivo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [mostrarResultados, setMostrarResultados] = useState(false);

  // Descargar plantilla Excel
  const descargarPlantilla = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/v1/users/plantilla/excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al descargar la plantilla');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_usuarios.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al descargar la plantilla');
    }
  };

  // Manejar selección de archivo
  const handleArchivoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar extensión
      if (!file.name.match(/\.(xlsx|xls)$/)) {
        alert('Por favor selecciona un archivo Excel (.xlsx o .xls)');
        e.target.value = '';
        return;
      }
      setArchivo(file);
      setResultado(null);
      setMostrarResultados(false);
    }
  };

  // Importar usuarios
  const handleImportar = async () => {
    if (!archivo) {
      alert('Por favor selecciona un archivo primero');
      return;
    }

    setCargando(true);
    setResultado(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('archivo', archivo);

      const response = await fetch(`${BACKEND_URL}/api/v1/users/importar/excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al importar usuarios');
      }

      setResultado(data.resultados);
      setMostrarResultados(true);
      
      // Si hubo usuarios creados exitosamente, notificar al padre
      if (data.resultados.exitosos.length > 0 && onImportSuccess) {
        onImportSuccess();
      }

    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error al importar usuarios');
    } finally {
      setCargando(false);
    }
  };

  // Limpiar y cerrar
  const handleCerrar = () => {
    setArchivo(null);
    setResultado(null);
    setMostrarResultados(false);
    const input = document.getElementById('archivo-excel');
    if (input) input.value = '';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">
        Importar Usuarios Masivamente
      </h3>

      {/* Instrucciones */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">📋 Instrucciones:</h4>
        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
          <li>Descarga la plantilla Excel haciendo clic en el botón</li>
          <li>Rellena los datos de los usuarios (los campos con * son obligatorios)</li>
          <li>La contraseña será el DNI de cada usuario por defecto</li>
          <li>Todos los usuarios se crearán como "trabajador" y habilitados</li>
          <li>Los usuarios se asignarán automáticamente a tu empresa</li>
          <li>Sube el archivo completado</li>
        </ol>
      </div>

      {/* Botón descargar plantilla */}
      <div className="mb-6">
        <button
          onClick={descargarPlantilla}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Descargar Plantilla Excel
        </button>
      </div>

      {/* Input de archivo */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Seleccionar archivo Excel
        </label>
        <div className="flex items-center gap-4">
          <input
            id="archivo-excel"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleArchivoChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {archivo && (
            <span className="text-sm text-green-600 font-medium">
              ✓ {archivo.name}
            </span>
          )}
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex gap-3">
        <button
          onClick={handleImportar}
          disabled={!archivo || cargando}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            !archivo || cargando
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {cargando ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Importando...
            </span>
          ) : (
            'Importar Usuarios'
          )}
        </button>

        {(archivo || resultado) && (
          <button
            onClick={handleCerrar}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Resultados */}
      {mostrarResultados && resultado && (
        <div className="mt-6 border-t pt-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4">
            Resultados de la Importación
          </h4>

          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-2xl font-bold text-gray-800">{resultado.total}</div>
              <div className="text-sm text-gray-600">Total procesados</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-600">{resultado.exitosos.length}</div>
              <div className="text-sm text-green-700">Usuarios creados</div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="text-2xl font-bold text-red-600">{resultado.errores.length}</div>
              <div className="text-sm text-red-700">Errores</div>
            </div>
          </div>

          {/* Usuarios exitosos */}
          {resultado.exitosos.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-green-700 mb-3">✓ Usuarios creados exitosamente:</h5>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                <ul className="space-y-2">
                  {resultado.exitosos.map((item) => (
                    <li key={item.fila} className="text-sm text-green-800">
                      <span className="font-medium">Fila {item.fila}:</span> {item.usuario.nombre} {item.usuario.apellidos} - {item.usuario.dni} ({item.usuario.email})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Errores */}
          {resultado.errores.length > 0 && (
            <div>
              <h5 className="font-semibold text-red-700 mb-3">✗ Errores encontrados:</h5>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                <ul className="space-y-3">
                  {resultado.errores.map((item, index) => (
                    <li key={index} className="text-sm">
                      <div className="font-medium text-red-800">Fila {item.fila}:</div>
                      <div className="text-red-600 ml-4">{item.error}</div>
                      {item.datos && (
                        <div className="text-gray-600 ml-4 text-xs mt-1">
                          {item.datos['Nombre*']} {item.datos['Primer Apellido*']} - {item.datos['DNI*']}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
